import { IconButton, Tooltip, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import ChartTemplateDetailsStep from './ChartTemplateDetailsStep/ChartTemplateDetailsStep';
import EditIcon from '@mui/icons-material/Edit';
import { ManageChartTemplateActionType } from '../ManageChartTemplateContext/ManageChartTemplateReducer';
import RootUrlEditionModal from './RootUrlEditionModal';
import SimpleModal from 'ui/Modals/SimpleModal';
import SyncIcon from '@mui/icons-material/Sync';
import Typography from '@mui/material/Typography';
import { useGetChartTemplate } from './hooks/getOneChartTemplate';
import { useGetRootChart } from './hooks/getRootChart';
import { useManageChartTemplateContext } from '../ManageChartTemplateContext/ManageChartTemplateContext';
import { useTranslation } from 'react-i18next';
import { useUpdateChartTemplate } from './hooks/updateChartTemplate';

type EditChartTemplateModalOpenProps = {
    chartTemplateKey: number;
    open: boolean;
    setOpen: (arg: boolean) => void;
};

const EditChartTemplateModalOpen = ({
    chartTemplateKey,
    open,
    setOpen,
}: EditChartTemplateModalOpenProps): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    const {
        state: {
            authorizedFunctions,
            authorizedSoftwares,
            description,
            preview,
            rootUrl,
            shouldUpdateSourceChartParams,
            status,
            title,
        },
        dispatch,
    } = useManageChartTemplateContext();
    const [isRootUrlEditionModalOpen, setIsRootUrlEditionModalOpen] =
        useState(false);

    const updateChartTemplateMutation =
        useUpdateChartTemplate(chartTemplateKey);

    const { refetch, data: rootChartdata } = useGetRootChart(rootUrl);
    // TODO [React Query v5, onSuccess deletion] Relying on useEffect for state syncing is deprecated.
    // With no better immediate solution, this is still the recommanded solution. It is still marginaly better
    // than onSuccess. See: https://github.com/TanStack/query/discussions/5279#discussioncomment-5641482
    useEffect(() => {
        if (rootChartdata) {
            dispatch({
                type: ManageChartTemplateActionType.ApplyRootChartValues,
                rootChart: rootChartdata,
                rootUrl: rootUrl,
                shouldUpdateTitle: false,
            });
        }
    }, [rootChartdata, dispatch, rootUrl]);

    const { data: chartTemplate } = useGetChartTemplate(chartTemplateKey);
    // TODO see the comment above.
    useEffect(() => {
        if (chartTemplate) {
            dispatch({
                type: ManageChartTemplateActionType.InitializeChartTemplate,
                chartTemplate: chartTemplate,
            });
        }
    }, [chartTemplate]);

    if (!chartTemplate) return <></>;

    const rootUrlActionsButtons = (
        <>
            <Tooltip
                title={t('support.chartsTemplates.createOrEditModal.refresh')}
            >
                <IconButton color="primary" onClick={() => refetch()}>
                    <SyncIcon />
                </IconButton>
            </Tooltip>
            <IconButton
                color="primary"
                onClick={() => setIsRootUrlEditionModalOpen(true)}
            >
                <EditIcon />
            </IconButton>
        </>
    );

    const handleClose = () => {
        if (
            window.confirm(
                t(
                    'support.chartsTemplates.createOrEditModal.leavingConfirmation',
                ),
            )
        ) {
            setOpen(false);
        }
    };

    const handleChartTemplateUpdate = () => {
        updateChartTemplateMutation.mutate({
            editChartTemplateData: {
                newChartTemplateData: {
                    authorizedFunctions,
                    authorizedSoftwares,
                    description,
                    preview,
                    rootUrl,
                    status,
                    title,
                },
                shouldUpdateSourceChartParams,
            },
        });
        setOpen(false);
    };
    return (
        <>
            <SimpleModal
                additionalButtons={[
                    <Button
                        key="click-update-template"
                        onClick={handleChartTemplateUpdate}
                        variant="contained"
                    >
                        {t('common.confirm')}
                    </Button>,
                ]}
                open={open}
                maxWidth="md"
                onClose={handleClose}
                PaperProps={{
                    sx: { backgroundColor: theme.palette.lightGrey },
                }}
                dialogTitle={
                    <>
                        <Typography variant="h4">
                            {t('support.chartsTemplates.edit')}
                        </Typography>
                        <Typography sx={{ mt: 2 }}>
                            {t(
                                'support.chartsTemplates.createOrEditModal.detailsForm.editSubtitle',
                            )}
                        </Typography>
                    </>
                }
            >
                <ChartTemplateDetailsStep
                    rootUrlActionsButtons={rootUrlActionsButtons}
                />
            </SimpleModal>
            {isRootUrlEditionModalOpen && (
                <RootUrlEditionModal
                    open={isRootUrlEditionModalOpen}
                    setOpen={setIsRootUrlEditionModalOpen}
                />
            )}
        </>
    );
};

export default EditChartTemplateModalOpen;
